import {html, LitElement} from 'lit';

// A filter-select supporting grouped options. The options array mixes plain
// entries ({value, label}) with groups ({value, label, options: [...]});
// groups render in bold with their options indented beneath them, and are
// themselves selectable. Checking a group checks and disables all its options
// — only the group's value is submitted (under group-name), so the server and
// the active-filter chips see one group selection rather than every member.
// Plain or individually checked options submit under name, alongside any
// selected groups.
export default class GroupedFilterSelect extends LitElement {

  static properties = {
    name: { type: String },
    groupName: { type: String },
    value: { type: Array },
    groupValue: { type: Array },
    options: { type: Array },
    searchText: { type: String },
  }

  // disable shadow dom to use global styles
  createRenderRoot() {
    return this;
  }

  constructor() {
    super();
    this.name = this.getAttribute("name");
    this.groupName = this.getAttribute("group-name");
    this.value = (JSON.parse(this.getAttribute("value")) || []).map(String);
    this.groupValue = (JSON.parse(this.getAttribute("group-value")) || []).map(String);
    this.options = JSON.parse(this.getAttribute("options")) || [];
    this.searchText = '';

    document.addEventListener('filter-item:close', () => {
      this.searchText = '';
      this.requestUpdate();
    })
  }

  groups() {
    return this.options.filter((option) => option.options);
  }

  // Values of options covered by a selected group
  coveredValues() {
    const covered = new Set();
    this.groups().forEach((group) => {
      if (this.groupValue.includes(String(group.value))) {
        group.options.forEach((option) => covered.add(String(option.value)));
      }
    });
    return covered;
  }

  handleGroupChange(e) {
    const groupValue = String(e.target.value);
    if (e.target.checked) {
      this.groupValue = [...this.groupValue, groupValue];
      // Options the group covers are represented by the group itself
      const group = this.groups().find((g) => String(g.value) === groupValue);
      const coveredValues = group.options.map((o) => String(o.value));
      this.value = this.value.filter((v) => !coveredValues.includes(v));
    } else {
      this.groupValue = this.groupValue.filter((v) => v !== groupValue);
    }
    this.dispatchChange();
  }

  handleOptionChange(e) {
    const optionValue = String(e.target.value);
    if (e.target.checked) {
      this.value = [...this.value, optionValue];
    } else {
      this.value = this.value.filter((v) => v !== optionValue);
    }
    this.dispatchChange();
  }

  dispatchChange() {
    this.requestUpdate();
    // Let the checkbox states settle before the search controller reads the form
    setTimeout(() => {
      this.dispatchEvent(new CustomEvent('filter-change', { detail: {}, bubbles: true }));
    }, 0);
  }

  handleSearch(e) {
    e.preventDefault();
    this.searchText = e.target.value;
    this.requestUpdate();
  }

  matchesSearch(label) {
    return !this.searchText || (label || '').toLowerCase().includes(this.searchText.toLowerCase());
  }

  optionCount() {
    return this.options.reduce((sum, option) => sum + 1 + (option.options ? option.options.length : 0), 0);
  }

  groupCheckbox(group) {
    return html`
      <label class="search--filter-select__filter-select__option search--filter-select__filter-select__option--group">
        <span>${group.label}</span>
        <input type="checkbox"
          name="${this.groupName}[]"
          value="${group.value}"
          .checked="${this.groupValue.includes(String(group.value))}"
          @change="${this.handleGroupChange}" />
      </label>
    `;
  }

  optionCheckbox(option, { nested } = { nested: false }) {
    const covered = this.coveredValues().has(String(option.value));
    return html`
      <label class="search--filter-select__filter-select__option ${nested ? "search--filter-select__filter-select__option--nested" : ""}">
        <span>${option.label}</span>
        <input type="checkbox"
          name="${this.name}[]"
          value="${option.value}"
          .checked="${covered || this.value.includes(String(option.value))}"
          ?disabled="${covered}"
          @change="${this.handleOptionChange}" />
      </label>
    `;
  }

  render() {
    return html`
      <div class="search--filter-select__filter-select">
        ${this.optionCount() >= 10 ? html`
          <input
            type="text"
            class="search--filter-select__filter-select__search-input"
            placeholder="Search..."
            .value="${this.searchText}"
            @input="${this.handleSearch}"
          />
        ` : ''}

        <div class="search--filter-select__filter-select__options">
          ${this.options.map((option) => option.options ? html`
            ${this.matchesSearch(option.label) ? this.groupCheckbox(option) : ''}
            ${option.options.map((child) => this.matchesSearch(child.label) ? this.optionCheckbox(child, { nested: true }) : '')}
          ` : (this.matchesSearch(option.label) ? this.optionCheckbox(option) : ''))}
        </div>
      </div>
    `;
  }
}

customElements.define('grouped-filter-select', GroupedFilterSelect);
